import React from 'react'
import { useDispatch } from 'react-redux'
import { Popover } from 'antd'
import { Link } from 'react-router-dom'
import type { ColumnType } from 'antd/es/table'
import shortenText from '../../features/arendeFordelning/components/tableUtils'
import TruncatedText from '../truncatedText/TruncatedText'
import { setAnsvarigPerson } from '../../store/ansvarigPersonSlice'
import { setActiveMenuItem } from '../../store/sideMenuSlice'
import '../../styles/flagTooltip.css'
import '../../styles/flagLink.css'

export interface AnsvarigHandläggareEntry {
  personnummer: string
  namn: string
  // Ligger handläggaren i tittarens scope? Styr om namnet är klickbart — utanför scope
  // saknar man behörighet till handläggarens Mina ärenden.
  in_scope: boolean
}

interface AnsvarigBearer {
  ansvarig_handläggare?: AnsvarigHandläggareEntry[]
}

const MINA_ÄRENDEN = '/app/mina-arenden'

// Cell-komponent (egen komponent för att få använda useDispatch). Speglar utförar-kolumnens
// mönster: ett namn → TruncatedText, flera → ljus popover med en rad per handläggare.
// Listans ordning sätts i backend (insatstyp → flest ärenden → namn), så [0] är den mest
// relevanta handläggaren.
const AnsvarigCell: React.FC<{ ansvariga?: AnsvarigHandläggareEntry[]; maxLength: number }> = ({ ansvariga, maxLength }) => {
  const dispatch = useDispatch()

  if (!ansvariga || ansvariga.length === 0) return <>Ingen</>

  // Mina ärenden läser vald handläggare ur Redux → sätt den innan navigeringen.
  const openMyCases = (h: AnsvarigHandläggareEntry) => () => {
    dispatch(setAnsvarigPerson({ ansvarigPersonId: h.personnummer, ansvarigPersonName: h.namn }))
    dispatch(setActiveMenuItem(MINA_ÄRENDEN))
  }

  const first = ansvariga[0]
  const rest = ansvariga.length - 1

  // En handläggare → cellen är själv länken (avkortning + tooltip-om-avkortad + klick).
  if (rest === 0) {
    return first.in_scope ? <TruncatedText text={first.namn} to={MINA_ÄRENDEN} onClick={openMyCases(first)} /> : <TruncatedText text={first.namn} />
  }

  // Flera → klick öppnar en ljus popover med en rad per handläggare (interaktivt innehåll hör
  // hemma i en panel, inte en hover-tooltip). Cellen är bara trigger.
  return (
    <Popover
      content={
        <>
          <div style={{ fontWeight: 500, marginBottom: 6 }}>Ansvarig handläggare</div>
          {ansvariga.map((h, i) => (
            <div key={i}>{h.in_scope ? <Link to={MINA_ÄRENDEN} onClick={openMyCases(h)}>{h.namn}</Link> : <span>{h.namn}</span>}</div>
          ))}
        </>
      }
      overlayClassName="flag-popover"
      placement="topLeft"
      trigger="click"
    >
      <span className="flag-ansvarig-cell" style={{ display: 'block', whiteSpace: 'nowrap', cursor: 'pointer' }}>
        <span className="flag-ansvarig-name">{shortenText(first.namn, maxLength)}</span>
        <span style={{ color: 'rgba(0, 0, 0, 0.45)', marginLeft: 4 }}>+{rest}</span>
      </span>
    </Popover>
  )
}

/**
 * Aktuell ansvarig handläggare för brukaren (hämtas live ur statdb, se
 * server/src/services/distribution/ansvarigHandläggare.ts).
 *
 * Filtrering sköts i vyernas Filter-popover (som övriga dimensioner sedan #33/#36/#37) —
 * kolumnen har bara sortering.
 */
export const ansvarigHandläggareColumn = <T extends AnsvarigBearer>(options?: {
  width?: number
  maxLength?: number
  sortOrder?: 'ascend' | 'descend' | null
}): ColumnType<T> => {
  const maxLength = options?.maxLength ?? 19
  return {
    title: 'Ansvarig handläggare',
    dataIndex: 'ansvarig_handläggare',
    key: 'ansvarig_handläggare',
    // 190 — samma som Namn och Utförare. Rubriken "Ansvarig handläggare" är 20 tecken och
    // behövde mer än 185 med sorteringspilen bredvid; de tre hålls lika för jämn rytm.
    width: options?.width ?? 190,
    ...(options && 'sortOrder' in options ? { sortOrder: options.sortOrder } : {}),
    showSorterTooltip: { overlayClassName: 'flag-tooltip', title: 'Brukarens aktuella ansvariga handläggare' },
    render: (_, record) => <AnsvarigCell ansvariga={record.ansvarig_handläggare} maxLength={maxLength} />,
    sorter: (a, b) => (a.ansvarig_handläggare?.[0]?.namn ?? '').localeCompare(b.ansvarig_handläggare?.[0]?.namn ?? ''),
  }
}
